import { rulesModalVisible } from "../../cb/components/userMenus/ui/rulesModal"
import { loginOverlayVisible } from "../../cb/ui/loginOverlay"
import { addEventListenerPoly } from "../addEventListenerPolyfill"
import { recordInteractionIfAffiliatePage } from "../affiliates"
import { isNotLoggedIn } from "../auth"
import { focusingInputFromKeypress } from "../customInput"
import { openTipCalloutRequest } from "../fullvideolib/userActionEvents"
import { isCharacterKey } from "../ischaracterkey"
import { KeyboardEventHandlerChain } from "../keyboardEventHandler"
import { modalBlockChatFocus } from "../modalComponent"
import { isShowingAnyUserContextMenu } from "../userContextMenu"
import { VideoMode, videoModeHandler } from "../videoModeHandler"
import { openDefaultTipCalloutRequest } from "./userActionEvents"
import Key = JQuery.Key
import type { ChatTabContainer } from "./chatTabContainer"
import type { IChatTabContainer } from "../../cb/ui/iChatTab"
import type { DraggableCanvas } from "../fullvideolib/draggableCanvas"

let allowChatInputAutoFocus = true
export function enableChatInputAutoFocus(enable: boolean): void {
    allowChatInputAutoFocus = enable
}
export function chatInputAutoFocusEnabled(): boolean {
    return allowChatInputAutoFocus
}

export function isElementInForm(el: Element): boolean {
    return Boolean((el as HTMLInputElement).form) || el.closest("form") !== null
}

export function isNonChatInputFocused(): boolean {
    return (
        document.activeElement !== undefined && document.activeElement !== null // IE
        && ["input", "textarea"].includes(document.activeElement.tagName.toLowerCase())
        && !loginOverlayVisible()
    )
}

export function bindEventListeners(draggableCanvas: DraggableCanvas, defaultModeChatTabContainer: ChatTabContainer, shouldExecuteHandlers = () => true): void {
    /**
     * Binds actions for certain key press events specific to Chat Room functionality.
     *
     * @param draggableCanvas - DraggableCanvas associated with interactive video player
     * @param defaultModeChatTabContainer - The ChatTabContainer associated with split player mode.
     * @param shouldExecuteHandlers - Function used to determine if the key handlers should be executed. Currently, is only intended to be used with the RmRmlstSpa feature.
     */
    const handlers = new KeyboardEventHandlerChain()

    const getChatTabContainer = () => {
        if (videoModeHandler.getVideoMode() === VideoMode.Split) {
            return defaultModeChatTabContainer
        } else {
            return draggableCanvas.chatWindow.chatTabContainer
        }
    }

    const focusChatInput = () => {
        getChatTabContainer().getCurrentTab().focusCurrentChatInput()
    }

    const isChatInputFocused = () => {
        return draggableCanvas.chatWindow.chatTabContainer.getCurrentTab().isInputFocused() || defaultModeChatTabContainer.getCurrentTab().isInputFocused()
    }

    let blockingModalsCnt = 0
    modalBlockChatFocus.listen((blocking) => {
        if (blocking) {
            blockingModalsCnt += 1
        } else {
            blockingModalsCnt -= 1
        }
    })

    handlers.addHandler({
        keyCode: Key.Tab,
        requiresCtrlOrMeta: false,
        handle: (event: KeyboardEvent) => {
            if (!isChatInputFocused() && isNonChatInputFocused() && draggableCanvas.focusedWindow !== draggableCanvas.tipWindow || loginOverlayVisible()) {
                return
            }
            if (!loginOverlayVisible()) {
                draggableCanvas.setFocusedWindow(draggableCanvas.chatWindow)
                const cycleChat = (chatTabContainer: IChatTabContainer) => {
                    if (event.shiftKey) {
                        chatTabContainer.cycleToPrevWindow()
                    } else {
                        chatTabContainer.cycleToNextWindow()
                    }
                }

                if (videoModeHandler.getVideoMode() === VideoMode.Split) {
                    cycleChat(draggableCanvas.chatWindow.chatTabContainer)
                    cycleChat(defaultModeChatTabContainer)
                } else {
                    cycleChat(defaultModeChatTabContainer)
                    cycleChat(draggableCanvas.chatWindow.chatTabContainer)
                }
                event.preventDefault()
                event.stopPropagation()
            }
        },
    })

    handlers.addHandler({
        keyCode: Key.Enter,
        requiresCtrlOrMeta: false,
        handle: event => {
            if (isChatInputFocused()) {
                if (isNotLoggedIn()) {
                    event.preventDefault()
                }
                return
            }
            draggableCanvas.setFocusedWindow(draggableCanvas.chatWindow)
        },
    })

    handlers.addHandler({
        keyCode: Key.Escape,
        requiresCtrlOrMeta: false,
        handle: event => {
            event.preventDefault()
            if (draggableCanvas.focusedWindow !== undefined && draggableCanvas.focusedWindow.closable) {
                draggableCanvas.removeChild(draggableCanvas.focusedWindow)
            }
            draggableCanvas.setFocusedWindow(undefined)
        },
    })

    handlers.addHandler({
        keyCode: Key.L,
        requiresCtrlOrMeta: true,
        handle: (event) => {
            if (videoModeHandler.getVideoMode() !== VideoMode.Split && draggableCanvas.focusedWindow !== draggableCanvas.chatWindow) {
                return
            }
            if (videoModeHandler.getVideoMode() === VideoMode.Split && !defaultModeChatTabContainer.pmTab.tabHasFocus()) {
                return
            }
            event.stopPropagation()
            event.preventDefault()
            recordInteractionIfAffiliatePage()
            draggableCanvas.chatWindow.chatTabContainer.closeCurrentPMSession()
            defaultModeChatTabContainer.closeCurrentPMSession()
        },
    })

    handlers.addHandler({
        keyCode: Key.S,
        requiresCtrlOrMeta: true,
        handle: (event) => {
            event.stopPropagation()
            event.preventDefault()
            recordInteractionIfAffiliatePage()
            if (videoModeHandler.getVideoMode() === VideoMode.Split || draggableCanvas.element.style.visibility === "hidden") {
                openDefaultTipCalloutRequest.fire({ usedCtrlS: true })
            } else {
                openTipCalloutRequest.fire({ usedCtrlS: true })
            }
        },
    })

    const ignoredKeys = [Key.Space, Key.PageDown, Key.PageUp]

    // eslint-disable-next-line complexity
    const shouldIgnoreKeyDown = (event: KeyboardEvent) => {
        if (!chatInputAutoFocusEnabled()) {
            return true
        }
        if (event.keyCode === Key.Escape || event.ctrlKey || event.metaKey) {
            return false
        }
        if (event.target !== null && isElementInForm(event.target as Element) && !isChatInputFocused()) {
            return true
        }
        if (loginOverlayVisible() || ignoredKeys.indexOf(event.keyCode) !== -1 || blockingModalsCnt !== 0 || rulesModalVisible() || isShowingAnyUserContextMenu()) {
            return true
        }
        return !isChatInputFocused() && isNonChatInputFocused()
    }

    addEventListenerPoly("keydown", document, (event: KeyboardEvent) => {
        if (shouldIgnoreKeyDown(event)) {
            return
        }
        if (draggableCanvas.focusedWindow !== undefined) {
            if (draggableCanvas.focusedWindow.handleKeyEvent(event)) {
                return
            }
        }

        if (shouldExecuteHandlers() && !handlers.execute(event)) {
            if (!event.ctrlKey && !event.metaKey && isCharacterKey(event.which)) {
                if (videoModeHandler.getVideoMode() !== VideoMode.Split) {
                    draggableCanvas.setFocusedWindow(draggableCanvas.chatWindow)
                }
                focusingInputFromKeypress.fire(event)
                focusChatInput()
            }
        }
    }, true)
}
